import { sendLog, type LogError } from './actions/backendCommon';
import { doRefreshToken } from './actions/backendAuth';
import { Store, AnyAction } from 'redux';
import type { RootState } from './types';
import type { AuthStoreLike } from './actions/backendAuth';
import { setBackendLogDispatch, type BackendLogLevel } from './logDispatch';

const LOG_LEVEL_ORDER: BackendLogLevel[] = ['error', 'warn', 'info', 'debug', 'log'];

function isLevelAllowed(messageLevel: string, configLevel: string): boolean {
    const msgIdx = LOG_LEVEL_ORDER.indexOf(messageLevel as BackendLogLevel);
    const cfgIdx = LOG_LEVEL_ORDER.indexOf(configLevel as BackendLogLevel);
    if (msgIdx === -1 || cfgIdx === -1) return true;
    return msgIdx <= cfgIdx;
}

let storeRef: Store<RootState, AnyAction> | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isLogErrorLike(value: unknown): value is Exclude<LogError, string | Error> {
    return isRecord(value) && typeof value.message === 'string';
}

function toLogOptions(options: unknown): Record<string, unknown> | undefined {
    return isRecord(options) ? options : undefined;
}

function toLogError(error: unknown): LogError {
    if (typeof error === 'string' || error instanceof Error) {
        return error;
    }

    if (isLogErrorLike(error)) {
        return error;
    }

    return 'Unknown error';
}

function getAuthStore(store: Store<RootState, AnyAction>): AuthStoreLike {
    const state = store.getState();
    const authState = state?.backend?.auth;
    return isRecord(authState) ? (authState as AuthStoreLike) : {};
}

export function initializeBackendRuntime(store: Store<RootState, AnyAction>): void {
    storeRef = store;
    const dispatchLog = (error: unknown, level = 'error', options: unknown = undefined) => {
        const configLevel = storeRef?.getState()?.backend?.config?.app?.logLevel ?? 'warn';
        if (!isLevelAllowed(level, configLevel)) return;
        storeRef?.dispatch(sendLog(storeRef.getState(), toLogError(error), level, toLogOptions(options)));
    };
    setBackendLogDispatch(dispatchLog);

    doRefreshToken(getAuthStore(store), true, store.dispatch);
}

export function disposeBackendRuntime(): void {
    setBackendLogDispatch(null);
    storeRef = undefined;
}
